import React, { useEffect, useState } from 'react';

import {
    BasePlanLaunchDate,
    GetBasePlanLaunchDatesInput,
    GetBasePlanLaunchDatesOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { isEqual } from 'lodash';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { initialBasePlanLaunchDateState } from 'src/data/BasePlanData';
import { IBasePlanLaunchDate, IBasePlanLaunchDateParams } from 'src/types/IBasePlanLaunchDate';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';
import { convertTimestampToYYYYMMDDInUTCTimezone } from 'src/util/dateTimeUtil';

import { BreadcrumbWrapper } from '../Breadcrumb';
import { ConfirmationModal } from '../ConfirmationModal';
import { StepTracker } from '../StepTracker';
import { BasePlanLaunchDateDetails } from './BasePlanLaunchDateDetails';
import { BasePlanLaunchDateReview } from './BasePlanLaunchDateReview';

export const AddBasePlanLaunchDate = () => {
    const [basePlanLaunchDate, setBasePlanLaunchDate] = useState<IBasePlanLaunchDate>(initialBasePlanLaunchDateState);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const { metadata, setToasts, setTopAlertMsg } = React.useContext(AppContext);
    const territories = Object.values(metadata.territoryMapping);
    const history = useHistory();

    const params: IBasePlanLaunchDateParams = useParams();
    const basePlanLaunchDateId = parseInt(params.basePlanLaunchDateId);
    const [isEditMode] = useState<boolean>(!!basePlanLaunchDateId);

    // Fetch base plan launch date for edit mode
    useEffect(() => {
        if (!isEditMode) return;

        contractConfigurationClient
            .getBasePlanLaunchDates(GetBasePlanLaunchDatesInput.clone({ basePlanLaunchDateId }))
            .subscribe(
                (response: GetBasePlanLaunchDatesOutput) => {
                    if (response.basePlanLaunchDates && response.basePlanLaunchDates.length > 0) {
                        const mappedBasePlanLaunchDate = mapResponseToBasePlanLaunchDate(
                            response.basePlanLaunchDates[0]
                        );
                        setBasePlanLaunchDate(mappedBasePlanLaunchDate);
                    } else {
                        const toast: IToast = {
                            id: 'base-plan-launch-date-not-found-toast',
                            timeout: DEFAULT_TOASTER_TIMEOUT,
                            message: 'Base plan launch date does not exist',
                            type: 'error'
                        };
                        setToasts([toast]);
                        history.push(ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX);
                    }
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getBasePlanLaunchDates unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, []);

    const mapResponseToBasePlanLaunchDate = (response: BasePlanLaunchDate) => {
        const territory = territories.find((territory) => territory.territoryCode === response.territory);

        return {
            basePlanLaunchDateId: response.basePlanLaunchDateId,
            basePlanId: response.basePlan?.basePlanId,
            basePlanName: response.basePlan?.planName || '',
            territoryIds: territory?.territoryId ? [territory.territoryId] : [],
            startDate: response.startDate ? convertTimestampToYYYYMMDDInUTCTimezone(response.startDate) : '',
            endDate: response.endDate ? convertTimestampToYYYYMMDDInUTCTimezone(response.endDate) : ''
        } as IBasePlanLaunchDate;
    };

    const basePath =
        ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX +
        (isEditMode
            ? ROUTES.BASE_PLAN_LAUNCH_DATES.EDIT + `/${basePlanLaunchDateId}`
            : ROUTES.BASE_PLAN_LAUNCH_DATES.ADD);

    const breadcrumbItems: IBreadcrumb[] = [
        { link: ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX, title: 'Base plan launch dates' },
        { link: '', title: isEditMode ? 'Edit base plan launch date' : 'Add base plan launch date' }
    ];

    const steps: IStepTrackerStep[] = [
        {
            stepId: ROUTES.BASE_PLAN_LAUNCH_DATES.STEP.DETAILS,
            stepTitle: isEditMode ? 'Edit details' : 'Launch date details',
            StepComponent: BasePlanLaunchDateDetails
        },
        {
            stepId: ROUTES.BASE_PLAN_LAUNCH_DATES.STEP.REVIEW,
            stepTitle: isEditMode ? 'Review and save' : 'Review and add',
            StepComponent: BasePlanLaunchDateReview
        }
    ];

    const { pathname } = useLocation();
    const step = `/${pathname.split('/').pop()}`;
    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const onNext = () => {
        history.push(`${basePath}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        history.push(`${basePath}${steps[currentStepIndex - 1].stepId}`);
    };

    const onCancel = () => {
        if (!isEqual(basePlanLaunchDate, {})) {
            setIsCancelModalOpen(true);
        } else {
            history.push(ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX);
        }
    };

    return (
        <Column alignmentHorizontal="center" spacingInset="500 500 600">
            <Column width={950} maxWidth="100%" spacing="500">
                <BreadcrumbWrapper breadcrumbItems={breadcrumbItems} />
                <Row alignmentVertical="top" wrap="down" widths={['fit', 'fill']}>
                    <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                        <StepTracker steps={steps} basePath={basePath} currentStepIndex={currentStepIndex} />
                    </Column>
                    <Column spacing="300" maxWidth="550px">
                        <Switch>
                            {steps.map(({ stepId, StepComponent }) => (
                                <Route key={stepId} path={`${basePath}${stepId}`}>
                                    <StepComponent
                                        basePlanLaunchDate={basePlanLaunchDate}
                                        setBasePlanLaunchDate={setBasePlanLaunchDate}
                                        onNext={onNext}
                                        onCancel={onCancel}
                                        onPrevious={onPrevious}
                                        isEditMode={isEditMode}
                                    />
                                </Route>
                            ))}
                        </Switch>
                    </Column>
                </Row>
            </Column>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title={isEditMode ? 'Cancel edit launch date' : 'Cancel add launch date'}
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText={isEditMode ? 'Cancel edit launch date' : 'Cancel add launch date'}
                primaryButtonOnClick={() => {
                    history.push(ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX);
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />
        </Column>
    );
};
